import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import { SearchResult } from "@mirage/service-dbx-api";
import { Recommendation } from "@mirage/shared/search/recommendation";
import { SearchFilter } from "@mirage/shared/search/search-filters";
import { Observable } from "rxjs";

export type PreviousPrompt = {
  uuid: string;
  prompt: string;
  time: number;
  type: PROMPT_TYPE;
};

export enum RelevanceRubric {
  File = "file",
  Message = "message",
  Category = "category",
  Image = "image",
}

export enum PROMPT_TYPE {
  BACKEND = "backend",
  FRONTEND = "frontend",
  CUSTOM = "custom",
}

export interface Item {
  uuid?: string;
  title: string;
  url?: string;
  preview?: string;
  // For files
  fileSize?: number;
  // For Chats
  membersCount?: number;
  // For snippets
  snippet?: string;
  parent?: string;
  // For messages
  sender?: string;
  sendTime?: string;
}

// Maps RelevanceRubric to subject strings for prompt generation
export const rubricToSubject: { [key in RelevanceRubric]: string } = {
  [RelevanceRubric.File]: "File",
  [RelevanceRubric.Message]: "Message",
  [RelevanceRubric.Category]: "Category",
  [RelevanceRubric.Image]: "Image",
};

export interface SearchRelevanceArgs {
  item: SearchResult | Recommendation;
  base64Image?: string;
  query: string;
  prompt?: string;
  rubric: RelevanceRubric;
  activeFilters?: SearchFilter[];
}

export interface Service {
  /**
   * Computes relevance score for a search result using an LLM
   */
  computeRelevance: (args: SearchRelevanceArgs) => Observable<string>;

  /**
   * Returns a list of previously used prompts
   *
   * @param rubric - The relevance rubric to get default prompt for
   * @returns Promise of PreviousPrompt objects
   */
  fetchRecentPrompts: (
    rubric: RelevanceRubric,
  ) => Promise<(PreviousPrompt & { type: PROMPT_TYPE })[]>;

  /**
   * Deletes a prompt from the cache
   *
   * @param uuid - The UUID of the prompt to delete
   * @param rubric - The relevance rubric to get default prompt for
   * @returns Promise of PreviousPrompt objects
   */
  deletePrompt: (
    uuid: string,
    rubric: RelevanceRubric,
  ) => Promise<(PreviousPrompt & { type: PROMPT_TYPE })[]>;

  /**
   * Gets the default prompt ID for a rubric
   *
   * @param rubric - The relevance rubric to get default prompt ID for
   * @returns Promise of the default prompt ID or null if not set
   */
  getDefaultPromptId: (rubric: RelevanceRubric) => Promise<string | null>;

  /**
   * Sets the default prompt ID for a rubric
   *
   * @param rubric - The relevance rubric to set default prompt ID for
   * @param promptId - The prompt ID to set as default, or null to clear
   */
  setDefaultPromptId: (
    rubric: RelevanceRubric,
    promptId: string | null,
  ) => Promise<void>;
}

export interface JudgeRelevanceEvent extends PAPEvent {
  class: "dash";
  action: "judge_relevance";
  object: "dash_search_result";
  properties: {
    startTimeMs: number;
    dashActionSurface: DashActionSurface;
    rubric: RelevanceRubric;
    prompt: string;
    query: string;
    itemId: string;
    itemTitle: string;
    rating: number;
    explanation: string;
  };
}

export interface MemoryCache<T> {
  all(): Readonly<T[]>;
  addItem(item: T): Promise<T[]>;
  removeItem(item: T): Promise<T[]>;
  clear(): void;
  count(): number;
  getDefaultPromptId(rubric: RelevanceRubric): Promise<string | null>;
  setDefaultPromptId(
    rubric: RelevanceRubric,
    promptId: string | null,
  ): Promise<void>;
}
